import * as React from "react"

import {
  useAccountingChartQuery,
  useAccountingJournalInfiniteQuery,
  useAccountingLedgerQuery,
  useAccountingStatementsQuery,
  useAccountingTrialBalanceQuery,
} from "@/features/finance/services/finance-query"
import type { JournalResponse } from "@/features/finance/model/accounting-reports-types"

import { AccountingEndpoint, StatementSection, type FinanceReportEntry } from "@/features/reports/model/report-registry-types"
import type { NormalizedReport } from "@/features/reports/model/normalized-report"
import {
  adaptChartOfAccounts,
  adaptLedger,
  adaptStatement,
  adaptTrialBalance,
} from "@/features/reports/services/adapters/accounting-adapters"
import { useTranslations } from "@/i18n"
import { useAccountingLabels } from "@/features/reports/services/adapters/use-accounting-labels"
import { JOURNAL_VIEW_PAGE_SIZE } from "@/features/reports/lib/journal-export"
import type { ReportConfigValues } from "@/features/reports/lib/report-config-url"
import { useResolvedPeriod } from "@/features/reports/lib/use-resolved-period"
import { useEngineTemplateResult } from "@/features/reports/services/use-engine-report"
import { useFinanceResourceReport } from "@/features/reports/services/use-finance-resource-report"
import { useFinanceReportScope } from "@/features/reports/services/use-finance-report-scope"
import { resolveReportRefusalKey } from "@/features/reports/lib/report-refusal"

export interface ReportDataResult {
  data: NormalizedReport | undefined
  /** Raw payload for custom-renderer reports (statements/journal). */
  native: unknown
  isLoading: boolean
  isError: boolean
  /**
   * The i18n key explaining a REFUSAL, when the failure was one (ADR-0024).
   *
   * A refusal and an error arrive identically — a rejected request — and telling them apart is the whole
   * difference between a product that looks governed and one that looks broken. Null for a real failure.
   */
  refusalReasonKey: string | null
  isFetching: boolean
  refetch: () => void
  currency: string
  /** Infinite-scroll controls — set only for paged custom renderers (the journal). Absent elsewhere. */
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
}

/**
 * Fetches a report's data as the normalized `ReportResult` the shell renders (or `native` for custom
 * renderers). Switches on `entry.dataSource.kind` + accounting endpoint; every hook is called
 * unconditionally (disabled via a null query when inactive) to satisfy the rules of hooks. Config values
 * (period preset, account) come from the URL. (Engine + finance-resource sources land in later phases.)
 */
export function useReportData(
  entry: FinanceReportEntry,
  config: ReportConfigValues = {},
  /** Free-text filter for the journal view (transaction reference / account). Ignored by other reports. */
  journalSearch?: string,
): ReportDataResult {
  const scope = useFinanceReportScope()
  const period = useResolvedPeriod(config.period)
  // The report table renders SELECT cells verbatim, so accounting enums must be translated before they
  // reach it — otherwise the chart of accounts shows raw values like "NET_ASSET" and "DEBIT". Shared with the
  // export, so the file and the screen name things identically.
  const accountingLabels = useAccountingLabels()
  const t = useTranslations()
  const source = entry.dataSource
  const base = scope.workspaceId ? { workspaceId: scope.workspaceId, branchId: scope.branchId, scope: scope.scope } : null

  const endpoint = source.kind === "accounting" ? source.endpoint : null
  // Which financial statement this catalog entry is (Balance Sheet / Income Statement / Cash Flow) — drives the
  // export adapter below. The on-screen viewer still renders the grouped cards from `native`.
  const statementSection = source.kind === "accounting" ? source.section : undefined
  const withPeriod = base ? { ...base, dateFrom: period.from, dateTo: period.to } : null

  const statementsQuery = useAccountingStatementsQuery(endpoint === AccountingEndpoint.Statements ? withPeriod : null)
  const trialBalanceQuery = useAccountingTrialBalanceQuery(endpoint === AccountingEndpoint.TrialBalance ? base : null)
  const ledgerQuery = useAccountingLedgerQuery(
    endpoint === AccountingEndpoint.Ledger && base && config.accountingAccountId
      ? { ...base, accountingAccountId: config.accountingAccountId, dateFrom: period.from, dateTo: period.to }
      : null,
  )
  // The VIEW infinite-scrolls one page at a time; the EXPORT pages through the whole journal separately (see
  // `lib/journal-export.ts`). `page` is injected by the infinite query — here we only set the page size.
  const journalQuery = useAccountingJournalInfiniteQuery(
    endpoint === AccountingEndpoint.Journal && base
      ? { ...base, limit: JOURNAL_VIEW_PAGE_SIZE, ...(journalSearch ? { search: journalSearch } : {}) }
      : null,
  )
  // Flatten the loaded pages back into the single `JournalResponse` shape the viewer consumes; `total` reflects
  // the full journal so the viewer knows there is more to load.
  const journalNative = React.useMemo<JournalResponse | undefined>(() => {
    if (endpoint !== AccountingEndpoint.Journal || !journalQuery.data) return undefined
    const pages = journalQuery.data.pages
    const last = pages[pages.length - 1]
    return {
      items: pages.flatMap((page) => page.items),
      total: last?.total ?? 0,
      page: last?.page ?? 1,
      limit: last?.limit ?? JOURNAL_VIEW_PAGE_SIZE,
    }
  }, [endpoint, journalQuery.data])
  const chartQuery = useAccountingChartQuery(endpoint === AccountingEndpoint.Chart ? base : null)

  // ADR-0013 (Reporting Platform Foundation) reporting-engine templates (called unconditionally; enabled only for the engine source).
  const engineTemplateId = source.kind === "engine-template" ? source.templateId : null
  const engine = useEngineTemplateResult(engineTemplateId, undefined, engineTemplateId !== null)

  // Finance-resource reports (catalog endpoints beyond the engine); enabled only for that source.
  const resourceReport = useFinanceResourceReport(source.kind === "finance-resource" ? source.resource : null, base, {
    fundId: config.fundId,
    activityId: config.activityId,
    dateFrom: period.from,
    dateTo: period.to,
  })

  // MEMOISED, and that is load-bearing — not a micro-optimisation.
  //
  // Adapting on every render produced a brand-new `rows` array each time, and TanStack Table's
  // `autoResetPageIndex` (on by default) resets to page 1 whenever `data` changes IDENTITY. So choosing page 2
  // rewrote the URL, which re-rendered, which built a new array, which reset the page back to 1 — the table
  // visibly shook and snapped back to the first 10 rows. The rows never changed; only their identity did.
  const data = React.useMemo<NormalizedReport | undefined>(() => {
    if (endpoint === AccountingEndpoint.TrialBalance && trialBalanceQuery.data) {
      return adaptTrialBalance(trialBalanceQuery.data)
    }
    if (endpoint === AccountingEndpoint.Ledger && ledgerQuery.data) {
      return adaptLedger(ledgerQuery.data, accountingLabels)
    }
    if (endpoint === AccountingEndpoint.Chart && chartQuery.data) {
      return adaptChartOfAccounts(chartQuery.data, accountingLabels)
    }
    // Statements render via a custom viewer (`native`), but we ALSO adapt them to a line/amount table so the
    // catalog card can export a CSV instead of being Print-only.
    if (endpoint === AccountingEndpoint.Statements && statementsQuery.data) {
      return adaptStatement(statementsQuery.data, statementSection ?? StatementSection.BalanceSheet, (key) => t(key as never))
    }
    return undefined
  }, [endpoint, trialBalanceQuery.data, ledgerQuery.data, chartQuery.data, statementsQuery.data, statementSection, accountingLabels, t])

  if (source.kind === "engine-template") {
    return {
      data: engine.data,
      native: engine.data,
      isLoading: engine.isLoading,
      isError: engine.isError,
      refusalReasonKey: resolveReportRefusalKey(engine.error),
      isFetching: engine.isFetching,
      refetch: () => void engine.refetch(),
      currency: scope.currency,
    }
  }

  if (source.kind === "finance-resource") {
    return {
      data: resourceReport.data,
      native: resourceReport.raw,
      isLoading: resourceReport.isLoading,
      isError: resourceReport.isError,
      refusalReasonKey: resolveReportRefusalKey(resourceReport.error),
      isFetching: resourceReport.isFetching,
      refetch: resourceReport.refetch,
      currency: scope.currency,
    }
  }

  // Journal is the one accounting endpoint on an infinite query — its shape (pages) differs from the plain
  // queries below, and it exposes the fetch-next-page controls the viewer's scroll sentinel needs.
  if (endpoint === AccountingEndpoint.Journal) {
    return {
      data: undefined,
      native: journalNative,
      isLoading: journalQuery.isLoading,
      isError: journalQuery.isError,
      refusalReasonKey: resolveReportRefusalKey(journalQuery.error),
      isFetching: journalQuery.isFetching,
      refetch: () => void journalQuery.refetch(),
      currency: scope.currency,
      fetchNextPage: () => void journalQuery.fetchNextPage(),
      hasNextPage: journalQuery.hasNextPage,
      isFetchingNextPage: journalQuery.isFetchingNextPage,
    }
  }

  const active =
    endpoint === AccountingEndpoint.Statements
      ? statementsQuery
      : endpoint === AccountingEndpoint.Ledger
        ? ledgerQuery
        : endpoint === AccountingEndpoint.Chart
          ? chartQuery
          : trialBalanceQuery

  return {
    data,
    native: active.data,
    isLoading: active.isLoading,
    isError: active.isError,
    refusalReasonKey: resolveReportRefusalKey((active as { error?: unknown }).error),
    isFetching: active.isFetching,
    refetch: () => void active.refetch(),
    currency: scope.currency,
  }
}
